import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Genre,
  LocationBucket,
  LocationLabels,
  brand,
} from "@mbb/config";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Field from "../components/Field";
import TextArea from "../components/TextArea";
import Select from "../components/Select";
import Checkbox from "../components/Checkbox";
import GenrePicker from "../components/GenrePicker";
import PhotoUpload from "../components/PhotoUpload";
import FormMessage from "../components/FormMessage";
import { ApiError, apiFetch } from "../lib/api";

type FormState = {
  fullName: string;
  preferredName: string;
  email: string;
  location: LocationBucket | "";
  photoUrl: string | null;
  currentRead: string;
  favoriteGenres: Genre[];
  booksPerMonth: string;
  bookSuggestion: string;
  whyJoin: string;
  referralSource: string;
  instagramHandle: string;
  acknowledgedFee: boolean;
  agreedToGuidelines: boolean;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const initial: FormState = {
  fullName: "",
  preferredName: "",
  email: "",
  location: "",
  photoUrl: null,
  currentRead: "",
  favoriteGenres: [],
  booksPerMonth: "",
  bookSuggestion: "",
  whyJoin: "",
  referralSource: "",
  instagramHandle: "",
  acknowledgedFee: false,
  agreedToGuidelines: false,
};

const LOCATION_OPTIONS = [
  { value: LocationBucket.MANTECA, label: LocationLabels.MANTECA },
  { value: LocationBucket.AREA_209, label: LocationLabels.AREA_209 },
  { value: LocationBucket.FARTHER_OUT, label: LocationLabels.FARTHER_OUT },
];

const BOOKS_OPTIONS = [
  { value: "<1", label: "Less than 1" },
  { value: "1", label: "About 1" },
  { value: "2-3", label: "2 to 3" },
  { value: "4+", label: "4 or more" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function clientValidate(form: FormState): FieldErrors {
  const e: FieldErrors = {};
  if (!form.fullName.trim()) e.fullName = "Your full name is required.";
  if (!form.email.trim()) e.email = "An email address is required.";
  else if (!EMAIL_REGEX.test(form.email.trim()))
    e.email = "That doesn't look like a valid email.";
  if (!form.location) e.location = "Pick where you're based.";
  if (!form.whyJoin.trim()) e.whyJoin = "Tell us a little about why.";
  if (!form.acknowledgedFee)
    e.acknowledgedFee = "Please acknowledge the membership fee.";
  if (!form.agreedToGuidelines)
    e.agreedToGuidelines = "Please agree to the community guidelines.";
  return e;
}

export default function Apply() {
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const set = <K extends keyof FormState>(k: K) => (v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Clear that field's error as soon as they touch it.
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  function focusFirstError() {
    // Lets the DOM update first so data-field-error attributes are present.
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[data-field-error="true"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setGeneralError(null);

    const clientErrors = clientValidate(form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setGeneralError(
        "A few things need attention before we can send this off.",
      );
      focusFirstError();
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      await apiFetch("/api/applications", {
        method: "POST",
        body: {
          fullName: form.fullName.trim(),
          preferredName: form.preferredName.trim() || null,
          email: form.email.trim(),
          location: form.location,
          photoUrl: form.photoUrl,
          currentRead: form.currentRead.trim() || null,
          favoriteGenres: form.favoriteGenres,
          booksPerMonth: form.booksPerMonth || null,
          bookSuggestion: form.bookSuggestion.trim() || null,
          whyJoin: form.whyJoin.trim(),
          referralSource: form.referralSource.trim() || null,
          instagramHandle: form.instagramHandle.trim() || null,
          acknowledgedFee: form.acknowledgedFee,
          agreedToGuidelines: form.agreedToGuidelines,
        },
      });
      navigate("/apply/success", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // Map server zod fieldErrors back to our state.
        const body = err.body as
          | {
              issues?: {
                fieldErrors?: Partial<Record<keyof FormState, string[]>>;
              };
            }
          | null;
        const fieldErrors = body?.issues?.fieldErrors;
        if (fieldErrors && Object.keys(fieldErrors).length > 0) {
          const next: FieldErrors = {};
          for (const [k, msgs] of Object.entries(fieldErrors)) {
            if (msgs && msgs.length > 0) {
              next[k as keyof FormState] = msgs[0];
            }
          }
          setErrors(next);
        }
        setGeneralError(err.message);
        focusFirstError();
      } else {
        setGeneralError("Something went wrong. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Header />
      <main
        className="container section"
        style={{ maxWidth: 720, paddingBottom: "5rem" }}
      >
        <div className="eyebrow">Apply</div>
        <h1
          className="display display--lg"
          style={{ marginTop: "1rem", marginBottom: "0.75rem" }}
        >
          tell us about you.
        </h1>
        <p className="lead" style={{ marginBottom: "2rem" }}>
          Four short sections. Two minutes. We read every word — promise.
        </p>

        <form onSubmit={onSubmit} noValidate>
          <Section title="The basics">
            <Field
              label="Full name"
              required
              autoComplete="name"
              value={form.fullName}
              onChange={(e) => set("fullName")(e.currentTarget.value)}
              error={errors.fullName}
            />
            <Field
              label="What your friends call you"
              hint="Optional — we'll use this in the directory if you set it."
              autoComplete="nickname"
              value={form.preferredName}
              onChange={(e) => set("preferredName")(e.currentTarget.value)}
              error={errors.preferredName}
            />
            <Field
              label="Email"
              required
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => set("email")(e.currentTarget.value)}
              error={errors.email}
            />
            <Select
              label="Where are you based?"
              required
              value={form.location}
              onChange={(e) =>
                set("location")(e.currentTarget.value as LocationBucket)
              }
              options={LOCATION_OPTIONS}
              placeholder="Pick one"
              error={errors.location}
            />
            <PhotoUpload
              value={form.photoUrl}
              onChange={set("photoUrl")}
            />
          </Section>

          <Section title="Your reading life">
            <Field
              label="What are you reading right now (or a recent favorite)?"
              value={form.currentRead}
              onChange={(e) => set("currentRead")(e.currentTarget.value)}
              maxLength={280}
              error={errors.currentRead}
            />
            <GenrePicker
              value={form.favoriteGenres}
              onChange={set("favoriteGenres")}
            />
            <Select
              label="Roughly how many books a month?"
              value={form.booksPerMonth}
              onChange={(e) => set("booksPerMonth")(e.currentTarget.value)}
              options={BOOKS_OPTIONS}
              placeholder="No pressure"
              error={errors.booksPerMonth}
            />
            <Field
              label="One book you'd love the club to read"
              value={form.bookSuggestion}
              onChange={(e) => set("bookSuggestion")(e.currentTarget.value)}
              maxLength={280}
              error={errors.bookSuggestion}
            />
          </Section>

          <Section title="A little about you">
            <TextArea
              label="What draws you to Manteca Book Besties?"
              required
              rows={4}
              value={form.whyJoin}
              onChange={(e) => set("whyJoin")(e.currentTarget.value)}
              maxLength={1000}
              error={errors.whyJoin}
            />
            <Field
              label="How did you hear about us?"
              value={form.referralSource}
              onChange={(e) => set("referralSource")(e.currentTarget.value)}
              maxLength={280}
              error={errors.referralSource}
            />
            <Field
              label="Instagram handle"
              hint="Optional. Just your handle, e.g. @jane."
              value={form.instagramHandle}
              onChange={(e) => set("instagramHandle")(e.currentTarget.value)}
              maxLength={40}
              error={errors.instagramHandle}
            />
          </Section>

          <Section title="The fine print">
            <Checkbox
              required
              checked={form.acknowledgedFee}
              onChange={(e) =>
                set("acknowledgedFee")(e.currentTarget.checked)
              }
              error={errors.acknowledgedFee}
            >
              I understand membership is {brand.membershipPriceLabel} and renews on
              the day I join. I can cancel any time from the portal.
            </Checkbox>
            <Checkbox
              required
              checked={form.agreedToGuidelines}
              onChange={(e) =>
                set("agreedToGuidelines")(e.currentTarget.checked)
              }
              error={errors.agreedToGuidelines}
            >
              I agree to keep it kind — no gatekeeping, no hot takes for sport,
              spoiler warnings are a courtesy.
            </Checkbox>
          </Section>

          {generalError && (
            <div style={{ marginTop: "0.5rem" }}>
              <FormMessage tone="error">{generalError}</FormMessage>
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting}
            style={{ width: "100%", marginTop: "0.5rem" }}
          >
            {submitting ? "Sending…" : "Submit application"}
          </button>
        </form>
      </main>
      <Footer />
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid var(--c-rule)",
        borderRadius: "var(--r-md)",
        padding: "clamp(1.25rem, 3vw, 1.75rem)",
        marginBottom: "1.25rem",
      }}
    >
      <h2
        className="display"
        style={{
          fontSize: "clamp(1.3rem, 3vw, 1.65rem)",
          marginTop: 0,
          marginBottom: "1.25rem",
          color: "var(--c-palm)",
        }}
      >
        {title.toLowerCase()}
      </h2>
      {children}
    </section>
  );
}
